import { FieldsMapLabels } from "@omnia/shared/fields/types";
import { CropId, groupBy, IDatalayerViewerFactory, IDatalayerZoneMapInstance, ILayerViewModel, isObject } from "../../../omnia"
import { IDataLayerType, NutrientGroup, NutrientType } from "../../../types"

type LayerZone = IDatalayerZoneMapInstance

import { SoilMapView } from "@omnia/shared/soil/mapView"
import { LayerViewModel } from "@omnia/shared/layers/layerViewModel";
import { NumericValuesViewer } from "@omnia/shared/layers/viewers/numeric";
import { nutrientColors } from "@omnia/shared/nutrition/colors";

export abstract class NutritionMapView extends SoilMapView {

    protected _nutrientKey: string;

    protected _layerType: IDataLayerType;
    protected _layerViewer: IDatalayerViewerFactory;

    protected _zoneOverrides: { [key: number]: any } = {};

    protected _zoneIndex: { [key: number]: LayerZone };

    protected _labels: FieldsMapLabels


    constructor(public name: string, public key: string, protected _nutrient: NutrientType) {
        super(name, key);
        this._nutrientKey = NutrientType[_nutrient];
    }

    get nutrient(): NutrientType {
        return this._nutrient;
    }

    checkNutrient(nutrient: NutrientType) {
        return (this._nutrient == nutrient);
    }

    protected abstract createLayers(): LayerViewModel[];

    public showLayers(visibilityFilter?: (l: LayerViewModel) => boolean, labels: FieldsMapLabels = this._labels) {

        super.showLayers(visibilityFilter)

        this.createZoneIndex()

        this.applyZoneOverrides()

        this.refreshViewers()

        this.setupLabels(labels)

    }

    protected refreshViewers() {

        if (!this._layers) return;

        let minValue = Number.MAX_VALUE;
        let maxValue = Number.MIN_VALUE;
        let distinctValues: number[];

        this._layers.filter(l => l.visible && l.mapLayer).forEach(l => {

            let viewer = l.mapLayer.getViewer();

            if (viewer instanceof NumericValuesViewer) {
                viewer.refresh();
                minValue = Math.min(minValue, viewer.minValue);
                maxValue = Math.max(maxValue, viewer.maxValue);
                distinctValues = viewer.distinctValues;
            }

        });

        if (minValue == maxValue) {

            this._colors = null;

        }
        else if (minValue < maxValue) {

            let colors = nutrientColors[this._nutrientKey].gradient.map(c => c.Colour);

            if (distinctValues && distinctValues.length && (distinctValues.length <= colors.length)) {

                let step = colors.length / distinctValues.length;

                this._colors = distinctValues.map((v, index) => {

                    let last = (index == distinctValues.length - 1);

                    return {
                        Colour: colors[last ? (colors.length - 1) : Math.floor(index * step)],
                        Value: v,
                        Index: null
                    }
                });

            }
            else {

                let step = (maxValue - minValue) / colors.length;
                let rounding = (this._nutrientKey == "Lime" ? 2 : 1);

                this._colors = colors.map((c, index) => {

                    return {
                        Colour: c,
                        Value: Math.round((minValue + (index * step)) * rounding) / rounding,
                        Index: null
                    }
                });

            }

        }

    }

    private createZoneIndex() {

        let zoneIndex: { [key: number]: LayerZone } = {};

        (this._layers || []).filter(l => l.mapLayer).forEach(l => {

            l.mapLayer.zones.forEach(z => {
                zoneIndex[z.zone.DataLayerZoneId] = z
            })
        });

        this._zoneIndex = zoneIndex;


    }

    public hideLayers() {

        let ids = Object.keys(this._zoneOverrides).map(key => Number(key));

        ids.forEach(id => {
            this.applyZoneOverrideInternal(id, null, false);
        })

        super.hideLayers();

        this._zoneIndex = undefined;

    }

    public getLayers(): ILayerViewModel[] {

        if (!this._layers) {
            this.createAndSetupLayers();
        }

        return this._layers;

    }

    protected createAndSetupLayers(): ILayerViewModel[] {

        let layers = this.createLayers();

        layers.forEach(i => {
            i.showTooltips = false;
            i.selectable = true;
        });

        this._layers = layers;

        return layers;

    }

    public refreshLayers() {

        let visible = this._visible;

        if (this._layers) {
            this.hideLayers();
            this._layers.forEach(l => l.dispose());
        }

        if (visible) {
            this.createAndSetupLayers();
            this.showLayers();
        }

    }

    public setupLabels(labels: FieldsMapLabels) {

        this._labels = labels

        if (this._layers) {

            const groups = groupBy(this._layers, l => l.fieldId)

            const getArea = (l: ILayerViewModel) => l.cropping ? l.cropping.area : 0

            groups.forEach(g => {

                const groupLayers = g.values.sort((a, b) => getArea(b) - getArea(b))

                groupLayers.forEach((l, index) => {
                    l.setLayerLabel(getLayerLabel(l, index ? undefined : labels))
                })

            })


        }

    }

    protected showLayer(l: LayerViewModel, visibilityFilter?: (l: LayerViewModel) => boolean) {
        l.setViewer(this._layerViewer);
        super.showLayer(l, visibilityFilter);
    }


    findZone(croppingId: number) {

        if (!croppingId) {
            return null;
        }

        let layers = this.getLayers();

        let layer = layers.find(l => l.croppingId == croppingId);

        if (layer && layer.mapLayer) {
            let features = layer.mapLayer.getFeatures();
            if (features.length == 1) {
                return features[0];
            }
        }
    }

    protected filterNutrientGroup<T extends { NutrientGroup: NutrientGroup, Nutrient?: NutrientType }>(items: T[]) {

        const value = Math.pow(2, this._nutrient - 1);

        return items
            .filter(r => r.NutrientGroup & value)
            .filter(r => (!r.Nutrient) || (r.Nutrient == this._nutrient))

    }

    protected abstract applyZoneOverride(zone: LayerZone, ovr: any, refresh: boolean);

    public setZoneOverride(id: number, ovr: any, refresh: boolean = true) {

        if (isObject(ovr)) {
            this._zoneOverrides[id] = ovr;
        }
        else {
            delete this._zoneOverrides[id];
        }

        this.applyZoneOverrideInternal(id, ovr, refresh);

    }

    private applyZoneOverrides() {

        if (this._zoneOverrides) {

            let ids = Object.keys(this._zoneOverrides).map(key => Number(key));

            ids.forEach(id => {
                let ovr = this._zoneOverrides[id];
                this.applyZoneOverrideInternal(id, ovr, true);
            })

        }
    }

    private applyZoneOverrideInternal(id: number, ovr: any, refresh: boolean) {

        if (this._zoneIndex) {

            let zone = this._zoneIndex[id];
            if (zone) {
                this.applyZoneOverride(zone, ovr, refresh);
            }
        }

    }

    public clearZoneOverrides() {

        let keys = Object.keys(this._zoneOverrides);
        let layerIds: number[] = [];

        keys.map(key => Number(key)).forEach(id => {

            this.setZoneOverride(id, undefined, false);

            if (this._zoneIndex) {

                let zone = this._zoneIndex[id];
                if (zone) {
                    layerIds.push(zone.layer.datalayerId);
                }

            }
        });

        if (layerIds.length && this._layers) {

            layerIds.forEach(id => {

                let layer = this._layers.find(l => l.datalayerId == id);
                if (layer) {
                    layer.refresh();
                }

            })

        }

    }
}

function getLayerLabel(layer: ILayerViewModel, labels: FieldsMapLabels) {

    const field = layer.field
    const crop = layer.cropping

    switch (crop && crop.cropId) {
        case CropId.GrassMargin:
            return null
    }

    if (field) {

        return [
            (labels & FieldsMapLabels.field) ? field.fieldName : null,
            (labels & FieldsMapLabels.crop) ? (crop.crop ? crop.crop.Name : null) : null,
            (labels & FieldsMapLabels.variety) ? (crop.variety ? crop.variety.Name : null) : null
        ].filter(t => !!t).join("\n")

    }
}
